import { cx } from '../atoms/cx.ts'
import { hasSkin } from '../tokens/skin.ts'

import { RotorLine } from './LineFigures.tsx'

import './figures.css'

/**
 * 원심 로터 — 와이어프레임 `rotorDraw`(1531행) · `.rotor` CSS 225–241행.
 *
 * 슬롯 4개가 마주보는 쌍(1↔3 · 2↔4)이다. 회전 중이어도 **도형이 바뀌지 않는다** —
 * 바깥 점선 링만 생긴다(§1-2 "상태에 따라 그림 형태가 바뀌면 안 된다").
 * 비어 있어도 4칸을 점선으로 그린다.
 */
export interface RotorSlotView {
  /** 슬롯 번호 1~4. 자리는 고정이다 */
  index: 1 | 2 | 3 | 4
  /** 그 슬롯에 들어간 챔버 칸 이름(`A3`) */
  slot?: string
  /** 시료 ID 표기(마스킹 끝난 값) */
  sublabel?: string
  loaded: boolean
  /** 지금 도는 중 */
  active?: boolean
  /** 판정 표식 — 없으면 안 그린다 */
  verdict?: 'pass' | 'fail' | null
  /** 불평형 등으로 지목된 슬롯 */
  flagged?: boolean
  /** 평형추(R″)가 든 자리 — Reference 회차에 REF 맞은편. 챔버가 아니라 채운 면으로 그린다(eric 2026-10-08 「무게추 원심분리기에서 안 돌아」) */
  counterweight?: boolean
}

export interface RotorFigureProps {
  slots: readonly RotorSlotView[]
  /** 회전 중이면 바깥 점선 링 */
  spinning?: boolean
  /** 그림 크기. lg = 포커스 뷰 · sm = 제어 화면 */
  size?: 'md' | 'lg' | 'sm'
  label: string
}

export function RotorFigure({ slots, spinning = false, size = 'md', label }: RotorFigureProps) {
  /* 통합 콘솔은 디자인팀 시안의 선 도면(`LineFigures.tsx`) — 스킨 없는 앱은 아래 종전 그림 그대로다 */
  if (hasSkin()) return <RotorLine label={label} size={size} slots={slots} spinning={spinning} />
  return (
    <div
      aria-label={label}
      className={cx('fig', 'fig-rotor', size !== 'md' && `fig-rotor--${size}`)}
      role="img"
    >
      <div
        className={cx('fig-rotor__disc', spinning && 'fig-rotor__disc--spinning')}
        data-spinning={spinning ? 'true' : undefined}
      >
        {spinning ? <div className="fig-rotor__spin" /> : null}
        <div className="fig-rotor__pair" />
        <div className="fig-rotor__pair fig-rotor__pair--h" />
        {slots.map((slot) => (
          <div
            className={cx(
              'fig-rotor__slot',
              `fig-rotor__slot--s${String(slot.index)}`,
              slot.loaded && 'fig-rotor__slot--loaded',
              slot.loaded && slot.active === true && 'fig-rotor__slot--now',
              slot.flagged === true && 'fig-rotor__slot--ng',
            )}
            key={slot.index}
          >
            {slot.loaded && slot.slot !== undefined ? (
              <span className="fig-rotor__lab">
                {slot.slot}
                <small>{slot.sublabel ?? ''}</small>
              </span>
            ) : null}
            {slot.verdict ? (
              <span
                className={cx(
                  'fig-rotor__verdict',
                  slot.verdict === 'fail' && 'fig-rotor__verdict--fail',
                )}
              >
                {slot.verdict === 'fail' ? '✗' : '✓'}
              </span>
            ) : null}
          </div>
        ))}
        {slots.map((slot) => (
          <span
            className={`fig-rotor__num fig-rotor__num--n${String(slot.index)}`}
            key={`n${String(slot.index)}`}
          >
            {slot.index}
          </span>
        ))}
        <div className="fig-rotor__hub" />
      </div>
    </div>
  )
}
